import { useEffect } from "react";
import { useRouter } from "next/router";
import { useReducedMotion } from "framer-motion";

/**
 * Crossfades the viewport across a route change, so navigating reads as one
 * page giving way to the next instead of a hard cut. See `pageTransition.scss`
 * for the crossfade itself and for why home -> shop is the pair that needed it.
 *
 * Built on the View Transitions API rather than by animating a React subtree:
 * the browser snapshots the outgoing viewport, holds paint while Next mounts
 * the new page, then composites the fade off the main thread. That is the only
 * approach that covers the whole swap — the chrome band collapsing, the scroll
 * offset resetting and the page content changing all land inside one snapshot,
 * where fading `<main>` alone would leave the chrome snapping around it.
 *
 * Pages Router exposes no hook between "the next page is ready" and "the DOM
 * has been replaced", so the transition opens on routeChangeStart and its
 * update callback is left pending until the navigation completes.
 */

/**
 * How long paint may stay suppressed waiting for the route.
 *
 * Measured on a production build, home <-> shop lands in 20-110ms; this leaves
 * room for the several-times-slower render a mid-range phone does, which a
 * tighter budget spends its whole life timing out on. Overshooting costs less
 * than it looks: the held frame is the page the user just clicked away from,
 * sitting still during latency they would have waited through anyway, so it
 * reads as loading rather than as a stall — but it does swallow scrolling,
 * which is what keeps this well under a second.
 *
 * Past the budget the transition is dropped and the navigation lands the way
 * it did before this hook existed. This is the number to revisit first if the
 * crossfade turns out to be missing on real hardware.
 *
 * A `next dev` build renders the shop page in 320-580ms and so sits outside
 * this on the way in — the crossfade wants a production build to be judged on.
 */
const HOLD_BUDGET_MS = 400;

export function usePageTransition() {
  const router = useRouter();
  const reducedMotion = useReducedMotion() === true;

  useEffect(() => {
    if (!("startViewTransition" in document)) return;
    /*
     * Reduced motion skips the hold, not just the fade. Shortening the
     * crossfade in CSS leaves the suppression untouched, so the page still
     * freezes for up to the whole budget and then shows a held snapshot over a
     * live page underneath — and `::view-transition` does not take pointer
     * events, so clicks in that window land on content the viewer cannot see.
     */
    if (reducedMotion) return;

    // True only while a transition is holding paint, which is the window a
    // second navigation must not open another one in — an orphaned update
    // callback freezes the page until its budget runs out. Once the hold is
    // released the crossfade is just an animation, and a navigation landing
    // mid-fade is free to start its own; the browser skips the one in flight,
    // which is the right read of the user having moved on.
    let holding = false;
    // Set once the navigation has landed. The browser calls the update
    // callback at its own next rendering opportunity, which a fast route
    // change beats — without this flag that callback would then wait on a
    // routeChangeComplete that has already been and gone, and every quick
    // navigation would sit out its whole budget instead of animating.
    let routeSettled = false;
    // Resolves the update callback, once the browser has gotten around to
    // asking for it.
    let release: (() => void) | null = null;
    let holdTimer: number | undefined;

    const settle = () => {
      routeSettled = true;
      holding = false;
      window.clearTimeout(holdTimer);
      const pending = release;
      release = null;
      pending?.();
    };

    const handleStart = (url: string, { shallow }: { shallow: boolean }) => {
      // A shallow push keeps the same page mounted, so there is nothing to
      // cross into.
      if (shallow || holding) return;
      /*
       * Anchor navigations own their own choreography and must not be held.
       * `useCrossPageAnchorGlide` lands the page at the top and then glides to
       * the target on a rAF — and rAF does not run while paint is suppressed,
       * so the release would fire first, snapshot the page already sitting at
       * the anchor, and swallow the glide behind a held frame.
       */
      if (url.includes("#")) return;

      holding = true;
      routeSettled = false;

      const transition = document.startViewTransition(
        () =>
          new Promise<void>((resolve) => {
            if (routeSettled) {
              resolve();
              return;
            }
            release = resolve;
          }),
      );

      /*
       * `skipTransition` rejects `ready` with an AbortError, and the budget
       * expiring is a routine outcome rather than a fault — under `next dev`
       * it is the normal path. Unhandled, each one surfaces in the dev overlay.
       */
      transition.ready.catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        console.error("Page transition failed to start", error);
      });
      transition.finished.catch((error: unknown) => {
        console.error("Page transition failed", error);
      });

      holdTimer = window.setTimeout(() => {
        transition.skipTransition();
        settle();
      }, HOLD_BUDGET_MS);
    };

    const handleComplete = () => {
      // A macrotask, deliberately, not `afterPaint`: requestAnimationFrame does
      // not fire at all while an update callback is pending — the browser stops
      // running rendering steps for a document whose paint is suppressed — so a
      // rAF-based release deadlocks and the navigation falls through to the
      // budget instead. A zero timeout still lands after Next's scroll restore,
      // which it applies in a layout effect during the commit this event
      // follows. It does NOT wait for the other route-change listeners here,
      // which are rAF-based and therefore starved until this releases — hence
      // the hash bail-out above, which is the only one whose work is visual.
      window.setTimeout(settle, 0);
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", settle);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", settle);
      // Unmounting mid-navigation must not leave paint suppressed.
      settle();
    };
  }, [router.events, reducedMotion]);
}
